// ============================================================
// mapLibrarySync - a session's saved maps, one document each.
//
// The library used to be ONE JSON blob under `shared/battlefieldLibrary`.
// An imported campaign brought a 10 MB one, Firestore's limit is 1 MiB,
// and the SDK retries a refused write for ever - so every write queued
// behind it (tokens, combatants, reaction prompts, heartbeats) never
// landed and nothing said why (v8.310.19). A document per map is the
// shape the waiting rooms already chose for the same reason, and it
// makes a single oversized map the only thing an oversized map breaks.
//
// Pure: the id derivation and the diff a write needs. Zero React.
// ============================================================
import type { Terrain } from './presets';
import { packTerrain, unpackTerrain } from './mapFile';
import { fnv1aHex } from '../../utils/fnv1a';
import { splitText, joinParts, PART_CHARS } from '../storage/parts';
import type { Origin } from '../assets/types';

export interface LibraryEntry {
  name: string;
  terrain: Terrain;
  /**
   * The asset library version this map was copied from (v11.71.0): the
   * session's copy remembers it, so its list can say a newer one waits. On
   * the ENTRY, never on the terrain - the terrain goes to the shared map and
   * into exports, where another account's asset id means nothing.
   */
  origin?: Origin;
}

/** Ids PocketBase accepts unencoded, and Firestore too. */
const STORABLE = /^[a-zA-Z0-9_-]+$/;
const MAX_PLAIN_ID = 120;

/**
 * The document id for a map, from its NAME - the library's identity,
 * since saving under an existing name overwrites it. Stable across
 * clients with no coordination, storable on both backends: the name as
 * is when it already is, else its UTF-8 hex, else (for a very long
 * name) a hash of it.
 */
export function libraryDocId(name: string): string {
  if (STORABLE.test(name) && name.length <= MAX_PLAIN_ID) return `map-${name}`;
  let hex = '';
  for (const b of new TextEncoder().encode(name)) hex += b.toString(16).padStart(2, '0');
  if (hex.length <= MAX_PLAIN_ID) return `map-x-${hex}`;
  return `map-h-${fnv1aHex(name)}`;
}

export interface LibraryDiff {
  /** Entries to write - new, or changed in content. */
  set: LibraryEntry[];
  /** Names to delete. */
  remove: string[];
}

/** What a write from `prev` to `next` has to touch, and nothing else. */
export function libraryDiff(prev: LibraryEntry[], next: LibraryEntry[]): LibraryDiff {
  const before = new Map(prev.map((e) => [e.name, JSON.stringify(e)]));
  const after = new Set(next.map((e) => e.name));
  return {
    set: next.filter((e) => before.get(e.name) !== JSON.stringify(e)),
    remove: prev.filter((e) => !after.has(e.name)).map((e) => e.name),
  };
}

// ── Storage: packed, and split when still too big ─────────────
//
// A saved map is held UNPACKED in memory (its grid a cell per key) and
// used to be written that way; the live terrain document has always
// packed its grid (gridCodec), which is a fraction of the size. Packed
// first, then split into parts under `PART_CHARS` when a map is still
// bigger than one document, and reassembled on read - so Firestore
// carries what PocketBase carries, which has no ceiling of this kind.
// The splitting itself is `engine/storage/parts`, shared with the asset
// library.

export interface LibraryDoc {
  /** `libraryDocId(name)` for part 0, `${that}--p${i}` for the rest. */
  id: string;
  name: string;
  /** How many parts the map is in; 1 when it fits. */
  parts: number;
  /** This document's index. */
  part: number;
  /** A slice of the packed entry's JSON. */
  data: string;
}

export function partDocId(name: string, part: number): string {
  const base = libraryDocId(name);
  return part === 0 ? base : `${base}--p${part}`;
}

/** The documents one entry is stored as. */
export function splitLibraryEntry(entry: LibraryEntry, partChars = PART_CHARS): LibraryDoc[] {
  const packed = {
    name: entry.name,
    terrain: packTerrain(entry.terrain),
    ...(entry.origin ? { origin: entry.origin } : {}),
  };
  const slices = splitText(JSON.stringify(packed), partChars);
  return slices.map((data, i) => ({
    id: partDocId(entry.name, i),
    name: entry.name,
    parts: slices.length,
    part: i,
    data,
  }));
}

export interface AssembledLibrary {
  entries: LibraryEntry[];
  /** Every document id each map currently occupies - what a rewrite or delete has to touch. */
  docIds: Map<string, string[]>;
}

/**
 * The library from its documents. A map whose parts have not all arrived
 * yet - a listener view taken mid-write - is left out rather than shown
 * torn; it appears whole on the next view.
 */
export function assembleLibrary(docs: LibraryDoc[]): AssembledLibrary {
  const entries: LibraryEntry[] = [];
  const docIds = new Map<string, string[]>();
  for (const [name, list] of partsByName(docs)) {
    docIds.set(
      name,
      list.map((d) => d.id),
    );
    const entry = assembleEntry(list);
    if (entry) entries.push(entry);
  }
  return { entries, docIds };
}

/** The readable documents, grouped by map name in arrival order. */
function partsByName(docs: LibraryDoc[]): Map<string, LibraryDoc[]> {
  const byName = new Map<string, LibraryDoc[]>();
  for (const d of docs) {
    if (!d || typeof d.name !== 'string' || typeof d.data !== 'string') continue;
    const list = byName.get(d.name) ?? [];
    list.push(d);
    byName.set(d.name, list);
  }
  return byName;
}

/**
 * One map from its parts, or null when a part is missing or the whole is
 * torn or corrupt - one bad map must not empty the library.
 *
 * ABSENT MEANS ONE PART, on both counts: a one-part map that lost its
 * part fields on the wire (PocketBase before v8.334.1) is whole.
 */
function assembleEntry(list: LibraryDoc[]): LibraryEntry | null {
  const json = joinParts(list);
  if (json === null) return null;
  try {
    const parsed = JSON.parse(json) as LibraryEntry;
    if (parsed && typeof parsed.name === 'string' && parsed.terrain) {
      const origin = parsed.origin && typeof parsed.origin.assetId === 'string' ? { origin: parsed.origin } : {};
      return { name: parsed.name, terrain: unpackTerrain(parsed.terrain), ...origin };
    }
  } catch {
    /* a torn or corrupt map is left out, not thrown */
  }
  return null;
}
